import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AddFile.css';

const AddFile = ({ onRun }) => {
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState('lab');

  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    const format = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    return { start: format(start), end: format(end) };
  };

  const [addedFrom, setAddedFrom] = useState('');
  const [addedTo, setAddedTo] = useState('');
  const [addedBy, setAddedBy] = useState('Either');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');
  const [dateError, setDateError] = useState("");

  const [includeClosed, setIncludeClosed] = useState(false);
  const [accountTags, setAccountTags] = useState('');
  const [caseTags, setCaseTags] = useState('');
  const [procedure, setProcedure] = useState('All');
  const [includeUnarrived, setIncludeUnarrived] = useState(false);
  const [includeOnHold, setIncludeOnHold] = useState(false);
  const [includeClosedCases, setIncludeClosedCases] = useState(false);
  const [format, setFormat] = useState('On Screen');

  const [caseStatus, setCaseStatus] = useState('');

  const [procedureOptions, setProcedureOptions] = useState([]);
  const [isLoadingProcedures, setIsLoadingProcedures] = useState(false);

  const navigate = useNavigate();

  // ================================================================
  // STATUS OPTIONS — grouped for better UX
  // Sources:
  //   1. Case.js status enum (main statuses)
  //   2. StatusLabel.js holdOptions (on-hold reasons)
  //   3. NOT_ON_HOLD_STATUSES list (from backend controller)
  // ================================================================
  const STATUS_GROUPS = [
    {
      label: '— All Statuses —',
      options: [
        { value: '', label: 'All Statuses' },
      ],
    },
    {
      label: 'Main Statuses',
      options: [
        { value: 'in progress', label: 'In Progress (Not On Hold)' },
        { value: 'on hold', label: 'On Hold' },
        { value: 'outsourced', label: 'Outsourced' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'delivered', label: 'Delivered' },
      ],
    },
    {
      label: 'On-Hold Reasons',
      options: [
        { value: 'metal try-in', label: 'Metal try-in' },
        { value: 'bite block', label: 'Bite block' },
        { value: 'die trim', label: 'Die trim' },
        { value: 'frame try-in', label: 'Frame try-in' },
        { value: 'problem with case', label: 'Problem with case' },
        { value: 'hold case', label: 'Hold Case' },
        { value: 'in lab - no date yet', label: 'In lab - no date yet' },
        { value: 'hold for model mod.', label: 'Hold for model mod.' },
        { value: 'hold for study mod.', label: 'Hold for study mod.' },
        { value: 'hold for work. mod.', label: 'Hold for work. mod.' },
        { value: 'hold for picture', label: 'Hold for picture' },
        { value: 'hold for shade', label: 'Hold for shade' },
        { value: 'hold for bite', label: 'Hold for bite' },
        { value: 'hold for components', label: 'Hold for components' },
        { value: 'hold for impression', label: 'Hold for Impression' },
        { value: 'hold for partial', label: 'Hold for partial' },
        { value: 'wax try-in', label: 'Wax try-in' },
        { value: 'dr. requested call', label: 'Dr. requested call' },
        { value: 'bisque bake try-in', label: 'Bisque bake try-in' },
      ],
    },
    {
      label: 'Other Statuses',
      options: [
        { value: 'not on hold', label: 'Not On Hold' },
        { value: 'hold released', label: 'Hold Released' },
        { value: 'release', label: 'Release' },
        { value: 'canceled', label: 'Canceled' },
        { value: 'preinvoiced', label: 'Pre-Invoiced' },
        { value: 'pre-invoiced', label: 'Pre-Invoiced (dashed)' },
        { value: 'invoiced', label: 'Invoiced' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'ready', label: 'Ready' },
        { value: 'ready to ship', label: 'Ready To Ship' },
      ],
    },
  ];

  // ================================================================
  // Load user info
  // ================================================================
  useEffect(() => {
    const storedLabId = localStorage.getItem('userLabId');
    const storedUserType = localStorage.getItem('userType');

    if (storedLabId) setLabId(storedLabId);
    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }
  }, []);

  // Default: current month
  useEffect(() => {
    const { start, end } = getCurrentMonthDates();
    setAddedFrom(start);
    setAddedTo(end);
  }, []);

  // ================================================================
  // Fetch accounts
  // ================================================================
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        let fetchedAccounts = [];

        if (userType === 'lab' && labId) {
          const response = await axios.get(
            `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
          );
          if (response.data.success) {
            fetchedAccounts = response.data.data || [];
          }
        } else {
          const [hmsRes, ddxRes] = await Promise.all([
            axios.get("https://hms.backend.hiragroupcloud.com/api/account/account"),
            axios.get("https://hms.backend.hiragroupcloud.com/api/users")
          ]);

          const hmsAccounts = (hmsRes.data.data || []).map(acc => ({
            accountId: acc.accountId || acc._id,
            account_name: acc.account_name || "Unnamed"
          }));

          const ddxAccounts = (ddxRes.data.users || []).map(user => ({
            accountId: user._id,
            account_name: user.practiceName || `${user.firstName} ${user.lastName}`
          }));

          fetchedAccounts = [...hmsAccounts, ...ddxAccounts];
        }

        const cleanAccounts = fetchedAccounts
          .filter(acc => acc && acc.account_name)
          .map(acc => ({
            accountId: acc.accountId,
            account_name: acc.account_name.trim()
          }));

        setAccounts(cleanAccounts);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setError('Accounts load nahi ho sake');
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (userType && (userType === 'admin' || labId)) {
      fetchAccounts();
    }
  }, [userType, labId]);

  // ================================================================
  // Fetch procedures for selected account
  // ================================================================
  useEffect(() => {
    const fetchProceduresForAccount = async () => {
      if (!selectedAccount) {
        setProcedureOptions([]);
        setProcedure('All');
        return;
      }

      setIsLoadingProcedures(true);
      try {
        let proceduresData = [];

        if (userType === 'lab' && labId) {
          try {
            const response = await fetch(
              `https://hms.backend.hiragroupcloud.com/api/procedures/lab-procedures/${labId}`,
              {
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                }
              }
            );

            if (!response.ok) {
              throw new Error(`Lab procedures API error: ${response.status}`);
            }

            const result = await response.json();

            if (result.success && result.data) {
              proceduresData = result.data;
            } else {
              const allProceduresResponse = await fetch(
                "https://hms.backend.hiragroupcloud.com/api/procedures/procedures"
              );
              const allResult = await allProceduresResponse.json();
              if (allResult.success && allResult.data) {
                proceduresData = allResult.data;
              }
            }
          } catch (labError) {
            console.error('Error fetching lab procedures, trying all procedures:', labError);
            try {
              const allProceduresResponse = await fetch(
                "https://hms.backend.hiragroupcloud.com/api/procedures/procedures"
              );
              const allResult = await allProceduresResponse.json();
              if (allResult.success && allResult.data) {
                proceduresData = allResult.data;
              }
            } catch (fallbackError) {
              console.error('Error fetching all procedures:', fallbackError);
            }
          }
        } else {
          try {
            const response = await fetch(
              "https://hms.backend.hiragroupcloud.com/api/procedures/procedures"
            );
            const result = await response.json();
            if (result.success && result.data) {
              proceduresData = result.data;
            }
          } catch (adminError) {
            console.error('Error fetching all procedures:', adminError);
          }
        }

        const options = proceduresData
          .filter(proc => proc && (proc.procedure_name || proc.name))
          .map(proc => ({
            value: proc._id || proc.procedure_code,
            label: proc.procedure_name || proc.name,
            procedure_code: proc.procedure_code,
          }));

        setProcedureOptions(options);
      } catch (err) {
        console.error('Error in fetchProceduresForAccount:', err);
        setProcedureOptions([]);
      } finally {
        setIsLoadingProcedures(false);
      }
    };

    fetchProceduresForAccount();
  }, [selectedAccount, labId, userType]);

  // ================================================================
  // Handlers
  // ================================================================
  const handleDateChange = (field, value) => {
    setDateError("");
    if (field === "from") setAddedFrom(value);
    if (field === "to") setAddedTo(value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "accounts") setSelectedAccount(value);
    else if (name === "addedBy") setAddedBy(value);
    else if (name === "accountTags") setAccountTags(value);
    else if (name === "caseTags") setCaseTags(value);
    else if (name === "procedure") setProcedure(value);
    else if (name === "format") setFormat(value);
    else if (name === "caseStatus") setCaseStatus(value);
  };

  // ================================================================
  // Submit — save clean filterData & trigger parent fetch
  // ================================================================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!addedFrom || !addedTo) {
      setDateError("Added From aur Added To dono zaroori hain");
      return;
    }

    // Build clean filterData — skip empty values so backend gets minimal params
    const filterData = {
      addedFrom,
      addedTo,
      addedBy: addedBy || 'Either',
      selectedAccount: selectedAccount || '',
      accountTags: accountTags ? accountTags.trim() : '',
      caseTags: caseTags ? caseTags.trim() : '',
      procedure: procedure || 'All',
      includeClosed: !!includeClosed,
      includeUnarrived: !!includeUnarrived,
      includeOnHold: !!includeOnHold,
      includeClosedCases: !!includeClosedCases,
      format: format || 'On Screen',
    };

    // Only add caseStatus when user picked a real status
    if (caseStatus && caseStatus.trim() !== '') {
      filterData.caseStatus = caseStatus.trim();
    }

    localStorage.setItem('filterData', JSON.stringify(filterData));

    if (onRun) {
      onRun(filterData); // pass the same data so parent doesn't re-read localStorage
    } else {
      navigate('/Addcasefile');
    }
  };

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <div className="addfile-outer">
      <div className="addfile-title">Parameters</div>

      {(error || dateError) && (
        <Alert variant={error ? "danger" : "warning"} className="addfile-alert">
          {dateError || error}
        </Alert>
      )}

      <div className="addfile-panel">
        <form onSubmit={handleSubmit}>

          {/* Added From */}
          <div className="addfile-row">
            <label className="addfile-label-col">
              Added From<span className="addfile-required">*</span>
            </label>
            <div className="addfile-input-col">
              <input
                type="date"
                value={addedFrom}
                onChange={(e) => handleDateChange("from", e.target.value)}
                className="addfile-input"
                required
              />
            </div>
          </div>

          {/* Added To */}
          <div className="addfile-row">
            <label className="addfile-label-col">
              Added To<span className="addfile-required">*</span>
            </label>
            <div className="addfile-input-col">
              <input
                type="date"
                value={addedTo}
                onChange={(e) => handleDateChange("to", e.target.value)}
                className="addfile-input"
                required
              />
            </div>
          </div>

          {/* Added By */}
          <div className="addfile-row">
            <label className="addfile-label-col">
              Added By<span className="addfile-required">*</span>
            </label>
            <div className="addfile-input-col">
              <select
                value={addedBy}
                name="addedBy"
                onChange={handleInputChange}
                className="addfile-select"
              >
                <option value="Either">Either</option>
                <option value="Lab">Lab</option>
                <option value="Account">Account</option>
              </select>
            </div>
          </div>

          {/* Accounts */}
          <div className="addfile-row">
            <label className="addfile-label-col">Accounts</label>
            <div className="addfile-input-col">
              {loadingAccounts ? (
                <span style={{ fontSize: '13px', paddingTop: '8px' }}>Loading accounts...</span>
              ) : (
                <>
                  <select
                    name="accounts"
                    value={selectedAccount}
                    onChange={handleInputChange}
                    className="addfile-select"
                  >
                    <option value="">Select accounts</option>
                    {accounts.map(acc => (
                      <option key={acc.accountId} value={acc.accountId}>
                        {acc.account_name}
                      </option>
                    ))}
                  </select>
                  <label className="addfile-checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeClosed}
                      onChange={(e) => setIncludeClosed(e.target.checked)}
                      className="addfile-checkbox"
                    />
                    Include Closed
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Account Tags */}
          <div className="addfile-row">
            <label className="addfile-label-col">Account Tags</label>
            <div className="addfile-input-col">
              <input
                type="text"
                name="accountTags"
                value={accountTags}
                onChange={handleInputChange}
                placeholder="No filter"
                className="addfile-input"
              />
            </div>
          </div>

          {/* Case Tags */}
          <div className="addfile-row">
            <label className="addfile-label-col">Case Tags</label>
            <div className="addfile-input-col">
              <input
                type="text"
                name="caseTags"
                value={caseTags}
                onChange={handleInputChange}
                placeholder="No filter"
                className="addfile-input"
              />
            </div>
          </div>

          {/* ============ STATUS FILTER ============ */}
          <div className="addfile-row">
            <label className="addfile-label-col">Status</label>
            <div className="addfile-input-col">
              <select
                name="caseStatus"
                value={caseStatus}
                onChange={handleInputChange}
                className="addfile-select"
              >
                {STATUS_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          {/* Procedure */}
          <div className="addfile-row">
            <label className="addfile-label-col">Procedure</label>
            <div className="addfile-input-col">
              <select
                name="procedure"
                value={procedure}
                onChange={handleInputChange}
                className="addfile-select"
                disabled={!selectedAccount || isLoadingProcedures}
              >
                <option value="All">All</option>
                {isLoadingProcedures ? (
                  <option disabled>Loading procedures...</option>
                ) : (
                  procedureOptions.map((proc) => (
                    <option key={proc.value} value={proc.value}>
                      {proc.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="addfile-checkbox-group">
            <div className="addfile-checkbox-row">
              <label className="addfile-checkbox-label">
                <input
                  type="checkbox"
                  checked={includeUnarrived}
                  onChange={(e) => setIncludeUnarrived(e.target.checked)}
                  className="addfile-checkbox"
                />
                Include Unarrived Cases
              </label>
            </div>
            <div className="addfile-checkbox-row">
              <label className="addfile-checkbox-label">
                <input
                  type="checkbox"
                  checked={includeOnHold}
                  onChange={(e) => setIncludeOnHold(e.target.checked)}
                  className="addfile-checkbox"
                />
                Include On Hold Cases
              </label>
            </div>
            <div className="addfile-checkbox-row">
              <label className="addfile-checkbox-label">
                <input
                  type="checkbox"
                  checked={includeClosedCases}
                  onChange={(e) => setIncludeClosedCases(e.target.checked)}
                  className="addfile-checkbox"
                />
                Include Closed Cases
              </label>
            </div>
          </div>

          {/* Format */}
          <div className="addfile-row">
            <label className="addfile-label-col">
              Format<span className="addfile-required">*</span>
            </label>
            <div className="addfile-input-col">
              <select
                name="format"
                value={format}
                onChange={handleInputChange}
                className="addfile-select"
              >
                <option value="On Screen">On Screen</option>
                <option value="PDF">PDF</option>
                <option value="Excel">Excel</option>
                <option value="CSV">CSV</option>
              </select>
            </div>
          </div>

          {/* Run button */}
          <div className="addfile-button-wrap">
            <button type="submit" className="addfile-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
              Run
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AddFile;